import {
  InfoBullet,
  InfoLink,
  InfoParagraph,
  InfoSection,
  PublicInfoShell
} from "@/components/PublicInfoShell";
import { Text } from "@/design/Text";
import { palette } from "@/design/tokens";

export default function PrivacyPolicyRoute() {
  return (
    <PublicInfoShell
      eyebrow="Last updated July 19, 2026"
      title="Privacy policy"
      intro="Recital Master is a private video service for recital families. This policy explains what information the service uses and why."
    >
      <InfoSection title="Information we handle">
        <InfoBullet>
          <Text variant="bodyStrong" color={palette.textSoft}>Account information</Text>,
          including your email address, account identifier, authentication status, and access window.
        </InfoBullet>
        <InfoBullet>
          <Text variant="bodyStrong" color={palette.textSoft}>App and device information</Text>,
          such as device type, model, app version, registered-device status, and recent account activity.
        </InfoBullet>
        <InfoBullet>
          <Text variant="bodyStrong" color={palette.textSoft}>Viewing information</Text>,
          including recital selections, playback progress, watched dances, saved dancers, and casting or download state.
        </InfoBullet>
        <InfoBullet>
          <Text variant="bodyStrong" color={palette.textSoft}>Purchase and access information</Text>,
          such as whether access is active, renewal status, and transaction references. Payment-card details are handled by our payment provider and are not stored by Recital Master.
        </InfoBullet>
        <InfoBullet>
          <Text variant="bodyStrong" color={palette.textSoft}>Diagnostics</Text>,
          such as errors, crashes, and security events needed to keep the service reliable and protect family accounts.
        </InfoBullet>
      </InfoSection>

      <InfoSection title="How we use information">
        <InfoParagraph>We use this information to authenticate your family account, provide the recital library you are entitled to watch, remember viewing progress and preferences, enforce device and stream limits, support casting and downloads, provide customer support, and protect the service from misuse.</InfoParagraph>
        <InfoParagraph>We do not sell personal information or use recital viewing activity for third-party advertising.</InfoParagraph>
      </InfoSection>

      <InfoSection title="Service providers">
        <InfoParagraph>Recital Master relies on service providers for cloud hosting and authentication, video delivery, transactional email, payment processing on the website, and error diagnostics. They process information only to provide those services under their own contractual and security obligations.</InfoParagraph>
        <InfoParagraph>Video may also be sent to a television or playback device when you deliberately use AirPlay, Google Cast, or another platform playback feature.</InfoParagraph>
      </InfoSection>

      <InfoSection title="Retention and deletion">
        <InfoParagraph>Account, device, preference, progress, and viewing records are deleted with the account. A content-free, pseudonymous deletion audit is kept for 30 days, then automatically purged. Standard server logs and short-lived, hashed abuse-prevention records expire on their own schedules.</InfoParagraph>
        <InfoParagraph>Stripe transaction, invoice, customer, and canceled-subscription records are retained for accounting and tax obligations, generally for six years from the end of the tax year they relate to, or longer only when legally required.</InfoParagraph>
        <InfoParagraph>You can delete your account in the app or use the request route on our <InfoLink href="/delete-account">account deletion page</InfoLink>.</InfoParagraph>
      </InfoSection>

      <InfoSection title="Children and recital video">
        <InfoParagraph>Recital Master is offered to parents and guardians and is not directed to children under 13. Recital footage may depict performers, including minors, but access to family recital libraries is private and controlled by the applicable recital access grant.</InfoParagraph>
      </InfoSection>

      <InfoSection title="Security and changes">
        <InfoParagraph>We use technical and organizational safeguards designed to protect account and playback information. No online service can guarantee absolute security.</InfoParagraph>
        <InfoParagraph>We may update this policy as the service changes. The latest version and effective date will remain available on this page.</InfoParagraph>
      </InfoSection>

      <InfoSection title="Contact">
        <InfoParagraph>Questions about this policy or your information can be sent to <InfoLink href="mailto:recitalmaster@gmail.com">recitalmaster@gmail.com</InfoLink>.</InfoParagraph>
      </InfoSection>
    </PublicInfoShell>
  );
}
